import { useEffect, useState } from 'react';
import { Switch, Redirect, useLocation } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { FormattedMessage, useIntl } from 'react-intl';
import loadable from '@loadable/component';
import { Workbox } from 'workbox-window';

// CONSTANTS
import { FLEET_PLANS } from 'constants/plans';

// CONTAINERS
import ViewContainer from 'containers/View';
import guest from 'containers/Guest';
import customer from 'containers/Contactless';
import authenticated from 'containers/Authenticated';
import FleetSettings from 'containers/FleetSettings';
import PlatformSettings from 'containers/PlatformSettings';
import AccountSettings from 'containers/AccountSettings';

import { FLASH_MESSAGE_TYPES } from 'constants/common';

// HOOKS
import { usePrevious } from '../hooks';

// STYLES
import GlobalStyles from 'styles/global';
import { Container as ModalContainer, Relative, CompactModal, CenteredContent } from 'styles/modals';
import { PrimaryButton } from 'components/Buttons';

// SELECTORS
import { selectDealershipSettings } from 'services/DealershipSettings/selectors';
import { selectNetworkError, selectUnauthorized } from 'services/Authentication/selectors';

// UTILS
import { intercom } from 'utils';

import { SentryErrorBoundRoute } from '../utils/sentry.utils';

// THUNKS
import { logoutUser, clearNetworkError } from 'services/Authentication/thunks';
import { useAppDispatch } from 'hooks/store';
import CustomerCheckInRoutes from './customerCheckInRoutes';

// LAZY IMPORTS
// Containers
const FlashMessage = loadable(() => import('components/FlashMessage'));
const NotFound = loadable(() => import('views/NotFound'));
const PasswordResetPage = loadable(() => import('views/PasswordResetPage'));
const RequestPasswordResetPage = loadable(() => import('views/RequestPasswordResetPage'));
const AccountActivation = loadable(() => import('views/Account/Activation'));

// Guest routes
const LoginPage = guest(loadable(() => import('views/LoginPage')));

// Customer-authenticated routes
const Contactless = customer(loadable(() => import('views/Contactless')));

// User-authenticated routes
const Agreement = authenticated(loadable(() => import('views/Agreement')));
const Agreements = authenticated(loadable(() => import('views/Agreements')));
const Customer = authenticated(loadable(() => import('views/Customer')));
const Customers = authenticated(loadable(() => import('views/Customers')));
const ESignature = authenticated(loadable(() => import('views/ESignature')));
const MailChimpRedirect = authenticated(loadable(() => import('views/MailChimpRedirect')));
const Planner = authenticated(loadable(() => import('views/Planner')));
const Valet = authenticated(loadable(() => import('views/Valet')));
const ValetDetails = authenticated(loadable(() => import('views/ValetDetails')));
const Vehicle = authenticated(loadable(() => import('views/Vehicle')));
const Vehicles = authenticated(loadable(() => import('views/Vehicles')));
const Dashboard = authenticated(loadable(() => import('views/Dashboard')));

const Routes = () => {
  const location = useLocation();
  const dispatch = useAppDispatch();
  const { formatMessage } = useIntl();

  const dealershipSettings = useSelector(selectDealershipSettings());
  const unauthorized = useSelector(selectUnauthorized());
  const networkError = useSelector(selectNetworkError());
  const plan = dealershipSettings.get('planId', FLEET_PLANS.standard);

  const previousPathname = usePrevious(location.pathname);

  const redirectToPlanHome = () =>
    [FLEET_PLANS.pro, FLEET_PLANS.pro_plus].includes(plan) ? (
      <Redirect to="/fleet/planner" />
    ) : (
      <Redirect to="/fleet/vehicles" />
    );

  const [updateAvailable, setUpdateAvailable] = useState(false);

  useEffect(() => {
    // Effect to build service-worker file and its updater
    if ('serviceWorker' in navigator && __ENABLE_SW__) {
      const wb = new Workbox('/service-worker.js');

      wb.addEventListener('waiting', () => {
        wb.addEventListener('controlling', () => {
          // controllerchange triggered by "SKIP_WAITING"
          // this ensures service worker is ready.
          window.location.reload();
        });

        console.log('A Fleet update is available.'); // eslint-disable-line

        setUpdateAvailable(true);
        window.skipWaiting = () => wb.messageSkipWaiting(); // postMessage to waiting SW only
      });

      wb.register();
    }
  }, []);

  useEffect(() => {
    // Effect to monitor the change of paths
    if (location.pathname !== previousPathname && previousPathname !== undefined) {
      // Navigation has occured

      // eslint-disable-next-line eqeqeq
      if (navigator.serviceWorker != undefined && __ENABLE_SW__) {
        // Since no browser-based navigation are made, we need to explicitely check for
        // SW update. See https://developer.chrome.com/docs/workbox/service-worker-lifecycle/#manually-triggering-update-checks

        navigator.serviceWorker.ready.then((registration) => {
          registration.update();
        });
      }

      if (!location.pathname.match(/(customer-signature|customer-checkin)/)) {
        // Prevent intercom from re-activating in customer facing part of the app.
        intercom.update(); // This call also wakes up intercom after a shutdown
      }

      if (updateAvailable && 'skipWaiting' in window && __ENABLE_SW__) {
        // Navigated and has an updateAvailable
        // Call `postMessage({type: 'SKIP_WAITING'})` on waiting SW
        setUpdateAvailable(false);
        window.skipWaiting();
      }
    }
  }, [location.pathname, previousPathname, updateAvailable]);

  const logout = () => {
    dispatch(logoutUser(true));
  };

  const isOutsideApp = RegExp(
    /(\/login|\/customer-signature|customer-checkin|\/password-reset|\/account-activation|\/account-reactivation)/
  ).test(location.pathname);

  const hideMainMenu = RegExp(
    /(\/login|\/customer-signature|customer-checkin|\/password-reset|\/account-activation|\/account-reactivation|\/esign)/
  ).test(location.pathname);

  const darkModePages = RegExp(/(\/public)/).test(location.pathname);

  return (
    <ViewContainer showMainMenu={!hideMainMenu}>
      <GlobalStyles responsive={isOutsideApp} darkMode={darkModePages} />
      <Switch>
        <SentryErrorBoundRoute path="/" render={redirectToPlanHome} exact />
        <SentryErrorBoundRoute path="/login" exact>
          <LoginPage />
        </SentryErrorBoundRoute>
        <SentryErrorBoundRoute path="/password-reset/:uid/:token" exact>
          <PasswordResetPage />
        </SentryErrorBoundRoute>
        <SentryErrorBoundRoute path="/password-reset" exact>
          <RequestPasswordResetPage />
        </SentryErrorBoundRoute>
        <SentryErrorBoundRoute path="/fleet/dashboard" exact>
          <Dashboard />
        </SentryErrorBoundRoute>
        <SentryErrorBoundRoute path="/fleet/planner" exact>
          <Planner />
        </SentryErrorBoundRoute>
        <SentryErrorBoundRoute path="/fleet/vehicles" exact>
          <Vehicles />
        </SentryErrorBoundRoute>
        <SentryErrorBoundRoute path="/fleet/vehicles/:id" exact>
          <Vehicle />
        </SentryErrorBoundRoute>
        <SentryErrorBoundRoute path="/fleet/customers" exact>
          <Customers />
        </SentryErrorBoundRoute>
        <SentryErrorBoundRoute path="/fleet/customers/:id" exact>
          <Customer />
        </SentryErrorBoundRoute>
        <SentryErrorBoundRoute path="/fleet/agreements" exact>
          <Agreements />
        </SentryErrorBoundRoute>
        <SentryErrorBoundRoute path="/fleet/valet" exact>
          <Valet />
        </SentryErrorBoundRoute>
        <SentryErrorBoundRoute path="/fleet/valet/:id" exact>
          <ValetDetails />
        </SentryErrorBoundRoute>
        <SentryErrorBoundRoute path="/fleet/agreements/:id" exact>
          <Agreement />
        </SentryErrorBoundRoute>
        <SentryErrorBoundRoute path="/fleet/agreements/:id/esign" exact>
          <ESignature />
        </SentryErrorBoundRoute>
        <SentryErrorBoundRoute path="/fleet/agreements/:id/customer-signature" exact>
          <Contactless />
        </SentryErrorBoundRoute>
        <SentryErrorBoundRoute path="/fleet/settings">
          <FleetSettings />
        </SentryErrorBoundRoute>
        <SentryErrorBoundRoute path="/fleet/public/customer-checkin">
          <CustomerCheckInRoutes />
        </SentryErrorBoundRoute>
        <SentryErrorBoundRoute path="/mailchimp-redirect">
          <MailChimpRedirect />
        </SentryErrorBoundRoute>
        <SentryErrorBoundRoute path="/settings">
          <PlatformSettings />
        </SentryErrorBoundRoute>
        <SentryErrorBoundRoute path={['/account-activation', '/account-reactivation']} exact>
          <AccountActivation />
        </SentryErrorBoundRoute>
        <SentryErrorBoundRoute path="/account">
          <AccountSettings />
        </SentryErrorBoundRoute>
        <SentryErrorBoundRoute path="*">
          <NotFound />
        </SentryErrorBoundRoute>
      </Switch>
      {unauthorized && !isOutsideApp && (
        <ModalContainer>
          <Relative>
            <CompactModal>
              <CenteredContent>
                <FormattedMessage id="flashMessage.unauthorized" />
                <br />
                <PrimaryButton onClick={logout}>
                  <FormattedMessage id="login" />
                </PrimaryButton>
              </CenteredContent>
            </CompactModal>
          </Relative>
        </ModalContainer>
      )}

      {networkError && (
        <FlashMessage
          timeout={-1}
          onClose={() => dispatch(clearNetworkError())}
          text={formatMessage(
            {
              id: networkError.status ? 'errors.serverError' : 'errors.networkError',
            },
            {
              msg: networkError.status ? networkError.data.errors?.[0] : networkError.data,
              code: networkError.status,
            }
          )}
          type={networkError.status ? FLASH_MESSAGE_TYPES.error : FLASH_MESSAGE_TYPES.warning}
          visible
        />
      )}
    </ViewContainer>
  );
};

export default Routes;
